import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, UserPlus, Loader, Users, Phone, UserX, UserCheck, MessageCircle, Trash2 } from "lucide-react";
import { SearchInput } from "./BroadcastAtoms";
import { AVATAR_COLORS } from "./constants";
import AddContactsView from "./AddContactsView";
import api from "../../services/api";
import { toast } from "react-toastify";

const ContactsPanel = ({ group, onClose }) => {
  const navigate = useNavigate();
  const [contacts, setContacts]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [filter, setFilter]         = useState("all");
  const [removing, setRemoving]     = useState(null);
  const [view, setView]             = useState("list");
  const [activeContact, setActive]  = useState(null);

  const loadContacts = () => {
    setLoading(true);
    api.get(`/group-members/${group.id}/customers`)
      .then((r) => setContacts(r.data.customers || []))
      .catch(() => toast.error("Failed to load contacts"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (group) loadContacts(); }, [group?.id]);

  const handleRemove = async (customerId) => {
    setRemoving(customerId);
    try {
      await api.delete(`/group-members/${group.id}/customer/${customerId}`);
      setContacts((prev) => prev.filter((c) => c.id !== customerId));
      toast.success("Contact removed from group");
    } catch {
      toast.error("Failed to remove contact");
    } finally {
      setRemoving(null);
    }
  };

  const filtered = contacts.filter((c) => {
    const q = search.toLowerCase();
    const matchSearch  = (c.name || "").toLowerCase().includes(q) || (c.phoneNumber || "").includes(q);
    const matchFilter  =
      filter === "all" ||
      (filter === "active"   && c.status !== false) ||
      (filter === "inactive" && c.status === false);
    return matchSearch && matchFilter;
  });

  const activeCount   = contacts.filter((c) => c.status !== false).length;
  const inactiveCount = contacts.filter((c) => c.status === false).length;
  const existingIds   = new Set(contacts.map((c) => c.id));

  const TABS = [
    { key: "all",      label: "All",      count: contacts.length, color: "text-gray-600" },
    { key: "active",   label: "Active",   count: activeCount,     color: "text-green-600" },
    { key: "inactive", label: "Inactive", count: inactiveCount,   color: "text-red-500"  },
  ];

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-20 md:hidden" onClick={onClose} />
      <div className="absolute md:relative top-0 right-0 bottom-0 z-30 w-80 bg-white flex flex-col shadow-xl md:shadow-none border-l border-gray-200">

        {/* Header */}
        <div className="bg-[#075e54] text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-sm font-semibold">{group.name}</p>
            <p className="text-[11px] text-white/70">{contacts.length} contacts</p>
          </div>
          <div className="flex items-center gap-1">
            {view === "list" && (
              <button
                onClick={() => setView("add")}
                className="flex items-center gap-1 px-2.5 py-1 bg-white/20 hover:bg-white/30 rounded-full text-xs font-medium transition-colors mr-1"
              >
                <UserPlus className="w-3.5 h-3.5" />
                Add
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/20 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {view === "add" ? (
          <AddContactsView
            group={group}
            existingIds={existingIds}
            onBack={() => setView("list")}
            onAdded={() => { setView("list"); loadContacts(); }}
          />
        ) : (
          <>
            {/* Filter tabs */}
            <div className="flex border-b border-gray-100 flex-shrink-0">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  className={`flex-1 py-2 text-xs font-medium transition-colors border-b-2 ${
                    filter === tab.key
                      ? "border-[#075e54] text-[#075e54]"
                      : "border-transparent text-gray-400 hover:text-gray-600"
                  }`}
                >
                  <span className={`block text-lg font-bold leading-tight ${filter === tab.key ? "text-[#075e54]" : tab.color}`}>
                    {tab.count}
                  </span>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="px-3 py-2 border-b border-gray-100 flex-shrink-0">
              <SearchInput value={search} onChange={setSearch} placeholder="Search by name or phone…" />
            </div>

            {/* Contact list */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <Loader className="w-5 h-5 animate-spin text-gray-300" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-center px-4">
                  <Users className="w-8 h-8 text-gray-200 mb-2" />
                  <p className="text-sm text-gray-400">
                    {contacts.length === 0
                      ? <span>No contacts yet. <button onClick={() => setView("add")} className="text-[#075e54] underline">Add some?</button></span>
                      : "No contacts match your search."}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {filtered.map((c, idx) => (
                    <div key={c.id ?? idx}>
                      <div
                        onClick={() => setActive((prev) => prev?.id === c.id ? null : c)}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 ${
                          c.status === false ? "bg-gray-300" : AVATAR_COLORS[(c.id || idx) % AVATAR_COLORS.length]
                        }`}>
                          {(c.name || "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{c.name || "—"}</p>
                          <div className="flex items-center gap-1 mt-0.5">
                            <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            <p className="text-xs text-gray-400 truncate">{c.phoneNumber || "—"}</p>
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          {c.status === false
                            ? <UserX className="w-3.5 h-3.5 text-red-400" />
                            : <UserCheck className="w-3.5 h-3.5 text-green-500" />}
                        </div>
                      </div>

                      {/* Inline action sheet */}
                      {activeContact?.id === c.id && (
                        <div className="mx-3 mb-2 rounded-xl bg-gray-50 border border-gray-200 overflow-hidden">
                          <p className="text-[11px] text-gray-400 px-3 pt-2 pb-1 font-medium truncate">
                            {c.name || c.phoneNumber}
                          </p>
                          <div className="flex border-t border-gray-200">
                            <button
                              onClick={() => {
                                setActive(null);
                                onClose();
                                navigate("/chats", { state: { openPhone: c.phoneNumber } });
                              }}
                              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-[#075e54] hover:bg-[#075e54]/5 transition-colors border-r border-gray-200"
                            >
                              <MessageCircle className="w-3.5 h-3.5" />
                              Open Chat
                            </button>
                            <button
                              onClick={() => { setActive(null); handleRemove(c.id); }}
                              disabled={removing === c.id}
                              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                            >
                              {removing === c.id
                                ? <Loader className="w-3.5 h-3.5 animate-spin" />
                                : <Trash2 className="w-3.5 h-3.5" />}
                              Remove from Group
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-gray-100 flex-shrink-0 bg-gray-50 flex items-center justify-between">
              <p className="text-xs text-gray-400">{filtered.length} of {contacts.length} contacts</p>
              <button
                onClick={() => setView("add")}
                className="flex items-center gap-1 text-xs text-[#075e54] font-medium hover:underline"
              >
                <UserPlus className="w-3.5 h-3.5" />
                Add contacts
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ContactsPanel;
